import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { BostaConnectionResponse } from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { queryKeys } from "@/lib/query/core/query-keys";
import { invalidateSellerBostaConnection } from "@/lib/query/core/invalidation";
import { resolveBostaErrorMessage } from "@/modules/settings/lib/bostaErrors";

type UseBostaConnectionQueryParams = {
  storeSlug: string;
  enabled?: boolean;
};

export function useBostaConnectionQuery({
  storeSlug,
  enabled = true,
}: UseBostaConnectionQueryParams) {
  const query = useQuery({
    queryKey: queryKeys.seller.bostaConnection(storeSlug),
    // The endpoint is store-scoped via the seller session cookie — storeSlug
    // is in the query key only for cache isolation, not the URL.
    queryFn: () =>
      apiJsonClient<BostaConnectionResponse>(endpoints.seller.bostaConnection),
    enabled: storeSlug.length > 0 && enabled,
  });

  return {
    connection: query.data?.connection ?? null,
    // Optimistic default while loading — consumers hide Bosta when false.
    platformEnabled: query.data?.platformEnabled ?? true,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

// Mutation hooks expose both the raw error (so callers that need the code
// for branching logic still have it) AND a pre-resolved user-facing
// `errorMessage` so callers don't have to remember to wrap every `.message`
// read in the resolver — previously every site was leaking raw backend
// phrasing. `onError` callback is plumbed through for callers that want
// to trigger toasts / Sentry from the mutation itself.

type SaveBostaApiKeyInput = {
  storeSlug: string;
  apiKey: string;
};

type MutationCallbacks = {
  onError?: (error: unknown) => void;
};

export function useSaveBostaApiKeyMutation(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: SaveBostaApiKeyInput) =>
      apiJsonClient<BostaConnectionResponse>(endpoints.seller.bostaConnection, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ apiKey: input.apiKey }),
      }),
    onSuccess: async (_data, variables) => {
      await invalidateSellerBostaConnection(queryClient, variables.storeSlug);
    },
    onError: (error) => callbacks?.onError?.(error),
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    errorMessage: mutation.error
      ? resolveBostaErrorMessage(mutation.error).message
      : null,
    reset: mutation.reset,
  };
}

type UpdateBostaActiveInput = {
  storeSlug: string;
  isActive: boolean;
};

export function useUpdateBostaActiveMutation(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: UpdateBostaActiveInput) =>
      apiJsonClient<BostaConnectionResponse>(
        endpoints.seller.bostaConnectionActive,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ isActive: input.isActive }),
        },
      ),
    onSuccess: async (_data, variables) => {
      await invalidateSellerBostaConnection(queryClient, variables.storeSlug);
    },
    onError: (error) => callbacks?.onError?.(error),
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    errorMessage: mutation.error
      ? resolveBostaErrorMessage(mutation.error).message
      : null,
  };
}

type RotateBostaWebhookInput = {
  storeSlug: string;
};

export function useRotateBostaWebhookMutation(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: RotateBostaWebhookInput) => {
      void input;
      return apiJsonClient<BostaConnectionResponse>(
        endpoints.seller.bostaWebhookRotate,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        },
      );
    },
    onSuccess: async (_data, variables) => {
      await invalidateSellerBostaConnection(queryClient, variables.storeSlug);
    },
    onError: (error) => callbacks?.onError?.(error),
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    errorMessage: mutation.error
      ? resolveBostaErrorMessage(mutation.error).message
      : null,
  };
}

type DisconnectBostaInput = {
  storeSlug: string;
};

export function useDisconnectBostaMutation(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: DisconnectBostaInput) => {
      void input;
      return apiJsonClient<BostaConnectionResponse>(
        endpoints.seller.bostaConnection,
        {
          method: "DELETE",
        },
      );
    },
    onSuccess: async (_data, variables) => {
      await invalidateSellerBostaConnection(queryClient, variables.storeSlug);
    },
    onError: (error) => callbacks?.onError?.(error),
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    errorMessage: mutation.error
      ? resolveBostaErrorMessage(mutation.error).message
      : null,
  };
}
